import React, { useState, useContext, useEffect, useRef } from "react";
import { Link as RouterLink } from "react-router-dom";
import Button from "../../components/Button";
import { Input } from "../../components/Form";
import Typography from "../../components/Typography";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import ColorModeContext from "../../layout/themeContext";
import useSettings from "../../hooks/useSettings";
import IconButton from "../../components/IconButton";
import { MdVisibility as Visibility, MdVisibilityOff as VisibilityOff } from "react-icons/md";
import { Helmet } from "react-helmet";
import BRFlag from "../../assets/brazil.png";
import USFlag from "../../assets/unitedstates.png";
import ESFlag from "../../assets/esspain.png";
import ARFlag from "../../assets/arabe.png";
import clsx from "clsx";
import { getBackendUrl } from "../../config";
import packageJson from "../../../package.json";

const languageOptions = [
    { value: "pt-BR", label: "Português", icon: BRFlag },
    { value: "en", label: "English", icon: USFlag },
    { value: "es", label: "Spanish", icon: ESFlag },
    { value: "ar", label: "عربي", icon: ARFlag },
];

// Trocar para true quando a internacionalização voltar a ser usada
const LANGUAGE_SELECTOR_ENABLED = false;

const Login = () => {
    const { colorMode } = useContext(ColorModeContext);
    const { appName, mode } = colorMode;
    const [user, setUser] = useState({ email: "", password: "" });
    const [showPassword, setShowPassword] = useState(false);
    const [allowSignup, setAllowSignup] = useState(false);
    const { getPublicSetting } = useSettings();
    const { handleLogin } = useContext(AuthContext);

    const [open, setOpen] = useState(false);
    const ref = useRef();
    const [enabledLanguages, setEnabledLanguages] = useState(["pt-BR", "en"]);
    const [backgroundLight, setBackgroundLight] = useState("");
    const [backgroundDark, setBackgroundDark] = useState("");

    const getCompanyIdFromUrl = () => {
        const urlParams = new URLSearchParams(window.location.search);
        const companyId = urlParams.get("companyId");
        return companyId ? parseInt(companyId) : null;
    };

    const handleChangeInput = (e) => {
        setUser({ ...user, [e.target.name]: e.target.value });
    };

    const togglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };

    const handlSubmit = (e) => {
        e.preventDefault();
        handleLogin(user);
    };

    useEffect(() => {
        const companyId = getCompanyIdFromUrl();

        getPublicSetting("userCreation", companyId)
            .then((data) => {
                setAllowSignup(data === "enabled");
            })
            .catch((error) => {
                console.log("Error reading setting", error);
            });

        getPublicSetting("enabledLanguages", companyId)
            .then((langs) => {
                let arr = ["pt-BR", "en"];
                try {
                    if (langs) arr = JSON.parse(langs);
                } catch {}
                setEnabledLanguages(arr);
            })
            .catch(() => {
                setEnabledLanguages(["pt-BR", "en"]);
            });

        getPublicSetting("appLogoBackgroundLight", companyId)
            .then((bgLight) => {
                if (bgLight) {
                    const backendUrl = getBackendUrl();
                    const fullUrl = backendUrl + "/public/" + bgLight;
                    console.log("Background light URL:", fullUrl);
                    setBackgroundLight(fullUrl);
                } else {
                    setBackgroundLight("");
                }
            })
            .catch((err) => {
                console.error("Erro ao carregar imagem de fundo clara:", err);
                setBackgroundLight("");
            });

        getPublicSetting("appLogoBackgroundDark", companyId)
            .then((bgDark) => {
                if (bgDark) {
                    const backendUrl = getBackendUrl();
                    const fullUrl = backendUrl + "/public/" + bgDark;
                    console.log("Background dark URL:", fullUrl);
                    setBackgroundDark(fullUrl);
                } else {
                    setBackgroundDark("");
                }
            })
            .catch((err) => {
                console.error("Erro ao carregar imagem de fundo escura:", err);
                setBackgroundDark("");
            });
    }, []);

    const current =
        languageOptions.find((opt) => opt.value === i18n.language) ||
        languageOptions[0];

    const handleSelect = (opt) => {
        i18n.changeLanguage(opt.value);
        localStorage.setItem("language", opt.value);
        setOpen(false);
        window.location.reload();
    };

    let finalBackground;

    const hasValidLightBg = backgroundLight && backgroundLight.trim() !== '';
    const hasValidDarkBg = backgroundDark && backgroundDark.trim() !== '';

    if (mode === "light") {
        if (hasValidLightBg) {
            finalBackground = `url(${backgroundLight})`;
        } else {
            finalBackground = "#ffffff";
        }
    } else {
        if (hasValidDarkBg) {
            finalBackground = `url(${backgroundDark})`;
        } else {
            finalBackground = "linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #020617 100%)";
        }
    }

    const textFieldTailwind =
        "!rounded-xl !bg-white/80 backdrop-blur-[10px] transition-all duration-300 hover:!bg-white/90 !border-[rgba(244,67,54,0.2)] hover:!border-[rgba(244,67,54,0.4)] focus:!bg-white focus:!border-[#f44336] focus:!border-2 focus:!shadow-[0_0_0_3px_rgba(244,67,54,0.1)] focus:!ring-0";

    return (
        <>
            <Helmet>
                <title>{appName || "AtendeChat"}</title>
                <link rel="icon" href="/favicon.ico" />
            </Helmet>

            <div
                className="w-screen h-screen flex flex-col items-center justify-center relative overflow-hidden"
                style={{
                    backgroundImage: finalBackground,
                    backgroundRepeat: "no-repeat",
                    backgroundSize: "cover",
                    backgroundPosition: "center center",
                    backgroundAttachment: "fixed",
                }}
            >
                {/* Seletor de idioma — oculto enquanto o app roda só em PT */}
                {LANGUAGE_SELECTOR_ENABLED && (
                <div
                    ref={ref}
                    className={clsx(
                        "fixed top-5 left-5 z-[1000] p-2 rounded-xl border backdrop-blur-[10px]",
                        mode === "dark"
                            ? "bg-white/10 border-white/20"
                            : "bg-white/80 border-slate-200"
                    )}
                >
                    <button
                        onClick={() => setOpen((o) => !o)}
                        className={clsx(
                            "flex items-center gap-2 bg-none border-none text-sm font-medium cursor-pointer transition-opacity hover:opacity-80",
                            mode === "dark" ? "text-white" : "text-slate-700"
                        )}
                    >
                        <img
                            src={current.icon}
                            alt={current.label}
                            className="w-5 h-[15px] rounded-sm"
                        />
                        {current.label}
                        <span>▾</span>
                    </button>

                    {open && (
                        <div
                            className={clsx(
                                "absolute top-full left-0 mt-2 rounded-xl border backdrop-blur-[20px] p-2 z-[1000] min-w-[140px]",
                                mode === "dark"
                                    ? "bg-[rgba(30,30,30,0.95)] border-white/20"
                                    : "bg-[rgba(255,255,255,0.95)] border-white/20"
                            )}
                            style={{ boxShadow: "0 10px 25px rgba(0,0,0,0.15)" }}
                        >
                            {languageOptions
                                .filter((opt) => enabledLanguages.includes(opt.value))
                                .map((opt) => (
                                    <button
                                        key={opt.value}
                                        onClick={() => handleSelect(opt)}
                                        className="flex items-center gap-2 w-full px-3 py-2 text-left text-sm font-medium cursor-pointer rounded-lg transition-all hover:bg-red-50 hover:text-red-500 text-slate-700 bg-none border-none"
                                    >
                                        <img
                                            src={opt.icon}
                                            alt={opt.label}
                                            className="w-5 h-[15px] rounded-sm"
                                        />
                                        {opt.label}
                                    </button>
                                ))}
                        </div>
                    )}
                </div>
                )}

                <div className="w-full max-w-[420px] z-10 absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 px-4 sm:px-0">
                    <div
                        className={clsx(
                            "flex flex-col items-center p-[40px_30px] sm:p-[30px_25px] rounded-[20px] sm:rounded-[16px] w-full mx-auto border backdrop-blur-[20px]",
                            mode === "dark"
                                ? "bg-[rgba(30,30,30,0.95)] border-white/10"
                                : "bg-[rgba(255,255,255,0.95)] border-white/20"
                        )}
                        style={{
                            boxShadow:
                                mode === "dark"
                                    ? "0 20px 40px rgba(0,0,0,0.3), 0 0 0 1px rgba(255,255,255,0.05)"
                                    : "0 20px 40px rgba(0,0,0,0.1), 0 1px 0 rgba(255,255,255,0.2) inset, 0 0 0 1px rgba(255,255,255,0.1)",
                        }}
                    >
                        <div>
                            <img
                                className="w-full max-w-[280px] h-auto max-h-[80px] mx-auto mb-5"
                                style={{ filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.1))" }}
                                alt="logo"
                                src={getBackendUrl() + "/public/farmazon_logo-fzn-original---.png"}
                            />
                        </div>

                        <form className="w-full mt-2" noValidate onSubmit={handlSubmit}>
                            <Input
                                containerClassName="mt-4 mb-2"
                                required
                                fullWidth
                                id="email"
                                label={i18n.t("login.form.email")}
                                name="email"
                                value={user.email}
                                onChange={handleChangeInput}
                                autoComplete="email"
                                autoFocus
                                className={textFieldTailwind}
                            />
                            <Input
                                containerClassName="mt-4 mb-2"
                                required
                                fullWidth
                                name="password"
                                label={i18n.t("login.form.password")}
                                type={showPassword ? "text" : "password"}
                                id="password"
                                value={user.password}
                                onChange={handleChangeInput}
                                autoComplete="current-password"
                                className={textFieldTailwind}
                                endIcon={
                                    <IconButton
                                        aria-label="toggle password visibility"
                                        onClick={togglePasswordVisibility}
                                        edge="end"
                                        style={{ color: "#6b7280" }}
                                    >
                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                }
                            />
                            <Button
                                type="submit"
                                className="w-full !rounded-xl !py-3 !mt-6 !mb-4 !text-base !font-semibold !normal-case !text-white !border-none !shadow-lg"
                                style={{
                                    background: "linear-gradient(45deg, #f44336, #d32f2f)",
                                    boxShadow: "0 4px 12px rgba(244, 67, 54, 0.25)",
                                    transition: "all 0.3s ease",
                                }}
                            >
                                {i18n.t("login.buttons.submit")}
                            </Button>
                            {allowSignup && (
                                <div className="flex justify-center">
                                    <RouterLink
                                        to="/signup"
                                        className="text-sm !text-red-500 !no-underline !font-semibold transition-all hover:!text-red-700 hover:!underline"
                                    >
                                        {i18n.t("login.buttons.register")}
                                    </RouterLink>
                                </div>
                            )}
                        </form>
                    </div>
                </div>

                <Typography
                    className={clsx(
                        "fixed bottom-5 right-5 z-[1000] rounded-lg border backdrop-blur-[10px] px-3 py-1.5 text-xs font-medium opacity-80 hover:opacity-100 transition-opacity",
                        mode === "dark"
                            ? "bg-white/10 border-white/20 text-slate-200"
                            : "bg-white/80 border-slate-200 text-slate-600"
                    )}
                >
                    v{packageJson.version}
                </Typography>
            </div>
        </>
    );
};

export default Login;
